import { Stack, Typography } from "@mui/material";
import React, { useCallback, useState } from "react";
import { IControlledMutator, Nullable } from "sonobello.utilities.react";

import StepperItem from "../../../../Components/CustomerStepper/StepperItem";
import { IMedicalFormItemProps } from "../Types/IMedicalFormItemProps";
import MedicalFormItemState from "../Types/MedicalFormItemState";
import MedicalFormState from "../Types/MedicalFormState";

export interface IComposableMedicalFormProps extends IControlledMutator<Nullable<MedicalFormState>> {
  /** The mutator items within the form which mutate the form's state. */
  formItems: React.FC<IMedicalFormItemProps>[];
}

/** A form which initializes its provided from items and observes their changes in state.
 * @remarks This form deliberately discards state updates and instead controls its own value. This is a
 * holdover from the previous implementation, but its not good practice. This should instead be an
 * {@link IInitializedMutator}.
 */
const ComposableMedicalForm: React.FC<IComposableMedicalFormProps> = ({ formItems, value, onChange }) => {
  const [formState, setFormState] = useState<MedicalFormState>(value ?? {});

  const handleChange = useCallback(
    (newValue: MedicalFormItemState) => {
      setFormState(oldState => {
        const newState = { ...oldState, [newValue.formId]: newValue.value };
        onChange?.(newState);
        return newState;
      });
    },
    [onChange]
  );

  return (
    <StepperItem>
      <Stack spacing={2}>
        <StepperItem>
          <Typography variant="body2">
            I have a list of medical questions; please let me know if any of these apply to you...
          </Typography>
        </StepperItem>
        {formItems.map((FormItem, index) => (
          <StepperItem key={index}>
            <FormItem
              formState={formState}
              onChange={value => handleChange(value)}
              setMedicalFormState={setFormState}
            />
          </StepperItem>
        ))}
      </Stack>
    </StepperItem>
  );
};

export default ComposableMedicalForm;
